/**
 * Sheet „Einheit anlegen" (Spec 02a, „Oberfläche · Sheet").
 *
 * Sichtbar ist nur die Bezeichnung; alles andere steckt hinter „Weitere
 * Angaben". Das Elternobjekt (Grundstück **oder** Gebäude — Spec 02a, „Die
 * erlaubte Hierarchie") kommt aus dem Kontext und steht nur als Text im Kopf.
 * Für `unit-duplicate` (Spec 02b) ist das Elternobjekt das des
 * Ausgangsdatensatzes, nicht der geöffnete Datensatz selbst.
 *
 * „Anlegen und noch eins" speichert seit Spec 02b optimistisch — derselbe
 * Ablauf wie `BuildingSheet`.
 */

import { useNavigate } from '@tanstack/react-router'
import { useRef, useState } from 'react'
import type { SubmitEvent } from 'react'

import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ApiError } from '@/lib/api'
import { areaToInputValue, parseAreaInput } from '@/features/properties/area'
import { useCreateUnit } from '@/features/properties/api'
import type { UnitCreate, UnitDetail } from '@/features/properties/api'
import { FIELD_CLASS, FormField } from '@/components/FormField'
import type { FieldError } from '@/components/FormField'
import { integerToInputValue, parseIntegerInput } from '@/features/properties/integer'
import { UsageTypeField } from '@/features/properties/UsageTypeField'

interface UnitSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly parentId: string
  readonly parentLabel: string
  /** Gesetzt für `unit-duplicate` (Spec 02b, „Schnellerfassung ·
   * Duplizieren"): belegt alle Felder außer der Bezeichnung vor. */
  readonly duplicateFrom?: UnitDetail
}

const DEFAULT_USAGE_TYPE = 'residential'

const EMPTY_FORM = {
  unitRef: '',
  usageType: DEFAULT_USAGE_TYPE,
  areaSqm: '',
  rooms: '',
  floor: '',
}

type FormValues = typeof EMPTY_FORM

function deriveDuplicateForm(source: UnitDetail): FormValues {
  return {
    unitRef: '',
    usageType: source.usage_type.value,
    areaSqm: source.area_sqm === null ? '' : areaToInputValue(source.area_sqm),
    rooms: source.rooms === null ? '' : areaToInputValue(source.rooms),
    floor: source.floor === null ? '' : integerToInputValue(source.floor),
  }
}

function toBody(parentId: string, form: FormValues): UnitCreate {
  return {
    parent_id: parentId,
    unit_ref: form.unitRef,
    // `usage_type` ist im Rumpf Pflicht, auch mit Server-Vorgabewert (Spec
    // 02a, „Alles andere ist optional") — der generierte Typ verlangt ihn.
    usage_type: form.usageType as UnitCreate['usage_type'],
    area_sqm: parseAreaInput(form.areaSqm),
    rooms: parseAreaInput(form.rooms),
    floor: parseIntegerInput(form.floor),
  }
}

export function UnitSheet({ open, onOpenChange, parentId, parentLabel, duplicateFrom }: UnitSheetProps) {
  const navigate = useNavigate()
  const createMutation = useCreateUnit()
  const unitRefRef = useRef<HTMLInputElement>(null)

  // Lazy Initialisierer statt `EMPTY_FORM`: Seit die Detailquery (ADR 0028)
  // erst mit dem Öffnen des Duplizieren-Sheets scharf geschaltet wird,
  // mountet es für „Duplizieren" erstmals bereits mit `open === true` — die
  // untere „Jedes Öffnen beginnt frisch"-Umschaltung sieht dann nie einen
  // Wechsel von `false` auf `true` und säht das Formular nie.
  const [form, setForm] = useState<FormValues>(() =>
    open && duplicateFrom !== undefined ? deriveDuplicateForm(duplicateFrom) : EMPTY_FORM,
  )
  const [isMoreOpen, setIsMoreOpen] = useState(() => open && duplicateFrom !== undefined)
  const [fieldError, setFieldError] = useState<FieldError | undefined>(undefined)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)

  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setForm(duplicateFrom !== undefined ? deriveDuplicateForm(duplicateFrom) : EMPTY_FORM)
      setIsMoreOpen(duplicateFrom !== undefined)
      setFieldError(undefined)
      setGeneralError(undefined)
    }
  }

  function updateField(name: keyof FormValues) {
    return (value: string) => {
      setForm((current) => ({ ...current, [name]: value }))
    }
  }

  function resetForAnother() {
    setForm(EMPTY_FORM)
    setIsMoreOpen(false)
    setFieldError(undefined)
    setGeneralError(undefined)
    unitRefRef.current?.focus()
  }

  async function submit(andAnother: boolean) {
    setFieldError(undefined)
    setGeneralError(undefined)

    try {
      const created = await createMutation.mutateAsync({ body: toBody(parentId, form), optimistic: andAnother })
      if (andAnother) {
        resetForAnother()
      } else {
        onOpenChange(false)
        await navigate({ to: '/objekt/$entityId', params: { entityId: created.id }, search: { tab: undefined } })
      }
    } catch (error) {
      if (error instanceof ApiError && error.field !== null) {
        setFieldError({ field: error.field, message: error.message })
      } else if (error instanceof ApiError) {
        setGeneralError(error.message)
      } else {
        setGeneralError('Die Verbindung zum Server ist unterbrochen.')
      }
    }
  }

  async function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    await submit(false)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          unitRefRef.current?.focus()
        }}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[480px] md:data-[side=right]:!max-w-[480px]"
      >
        <form onSubmit={(event) => void handleSubmit(event)} className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">Einheit anlegen</SheetTitle>
            <p data-testid="unit-create-parent" className="text-label text-muted">
              {parentLabel}
            </p>
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
            <FormField
              ref={unitRefRef}
              label="Bezeichnung"
              testId="unit-create-unit-ref"
              apiField="unit_ref"
              errorPrefix="unit-create"
              required
              value={form.unitRef}
              onChange={updateField('unitRef')}
              error={fieldError}
            />

            <details
              open={isMoreOpen}
              onToggle={(event) => {
                setIsMoreOpen(event.currentTarget.open)
              }}
            >
              <summary
                data-testid="unit-create-more"
                className="text-label text-muted min-h-(--tap-min) cursor-pointer list-item py-2"
              >
                Weitere Angaben
              </summary>

              <div className="flex flex-col gap-4 pt-2">
                <UsageTypeField
                  testId="unit-create-usage-type"
                  errorPrefix="unit-create"
                  value={form.usageType}
                  onChange={updateField('usageType')}
                  error={fieldError}
                />
                <FormField
                  label="Fläche (m²)"
                  testId="unit-create-area-sqm"
                  apiField="area_sqm"
                  errorPrefix="unit-create"
                  inputMode="decimal"
                  value={form.areaSqm}
                  onChange={updateField('areaSqm')}
                  error={fieldError}
                />
                <FormField
                  label="Zimmer"
                  testId="unit-create-rooms"
                  apiField="rooms"
                  errorPrefix="unit-create"
                  inputMode="decimal"
                  value={form.rooms}
                  onChange={updateField('rooms')}
                  error={fieldError}
                />
                <FormField
                  label="Etage"
                  testId="unit-create-floor"
                  apiField="floor"
                  errorPrefix="unit-create"
                  inputMode="numeric"
                  value={form.floor}
                  onChange={updateField('floor')}
                  error={fieldError}
                />
              </div>
            </details>
          </div>

          <SheetFooter>
            {generalError !== undefined && (
              <p data-testid="unit-create-error" role="alert" className="text-label text-status-overdue">
                {generalError}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="unit-create-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              <button
                type="button"
                data-testid="unit-create-save-another"
                disabled={createMutation.isPending}
                aria-busy={createMutation.isPending}
                onClick={() => void submit(true)}
                className={`text-text px-4 ${FIELD_CLASS} disabled:opacity-60`}
              >
                Anlegen und noch eins
              </button>
              <button
                type="submit"
                data-testid="unit-create-save"
                disabled={createMutation.isPending}
                aria-busy={createMutation.isPending}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                Einheit anlegen
              </button>
            </div>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  )
}
